/**
 * Firestore persistence for Thescon AI chat threads.
 * Collection: threads/{threadId} with subcollection messages/{messageId}.
 */
import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type Firestore,
  type Unsubscribe,
} from 'firebase/firestore';
import type { ThesconAiScopeConfig } from '../components/ThesconAiScopePanel';

export const THESCON_AI_THREADS_COLLECTION = 'threads';

export type ThreadDoc = {
  ownerId: string;
  scope: ThesconAiScopeConfig;
  title: string;
  createdAt: unknown;
  updatedAt: unknown;
};

export type ThreadMessageDoc = {
  role: 'user' | 'assistant';
  content: string;
  createdAt: unknown;
  retrievalJobId?: string;
};

function messagesCollectionRef(db: Firestore, threadId: string) {
  return collection(db, THESCON_AI_THREADS_COLLECTION, threadId, 'messages');
}

export async function createThread(
  db: Firestore,
  ownerId: string,
  scope: ThesconAiScopeConfig,
  title: string
): Promise<string> {
  const threadRef = doc(collection(db, THESCON_AI_THREADS_COLLECTION));
  await setDoc(threadRef, {
    ownerId,
    scope,
    title: title.slice(0, 200),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return threadRef.id;
}

export async function touchThreadAfterMessage(
  db: Firestore,
  threadId: string,
  preview: string,
  options?: { scope?: ThesconAiScopeConfig }
): Promise<void> {
  await updateDoc(doc(db, THESCON_AI_THREADS_COLLECTION, threadId), {
    updatedAt: serverTimestamp(),
    lastMessagePreview: preview.slice(0, 280),
    ...(options?.scope ? { scope: options.scope } : {}),
  });
}

export async function saveThreadScope(
  db: Firestore,
  threadId: string,
  scope: ThesconAiScopeConfig
): Promise<void> {
  await updateDoc(doc(db, THESCON_AI_THREADS_COLLECTION, threadId), {
    scope,
    updatedAt: serverTimestamp(),
  });
}

export type ThreadListItem = {
  id: string;
  title: string;
  updatedAt: unknown;
  lastMessagePreview?: string;
};

/** Sidebar / picker: newest threads first. Requires composite index ownerId + updatedAt. */
export function subscribeUserThreads(
  db: Firestore,
  ownerId: string,
  maxThreads: number,
  onThreads: (rows: ThreadListItem[]) => void
): Unsubscribe {
  const q = query(
    collection(db, THESCON_AI_THREADS_COLLECTION),
    where('ownerId', '==', ownerId),
    orderBy('updatedAt', 'desc'),
    limit(maxThreads)
  );
  return onSnapshot(
    q,
    (snap) => {
      const rows: ThreadListItem[] = [];
      snap.forEach((d) => {
        const data = d.data();
        rows.push({
          id: d.id,
          title: typeof data.title === 'string' ? data.title : 'Chat',
          updatedAt: data.updatedAt,
          lastMessagePreview:
            typeof data.lastMessagePreview === 'string' ? data.lastMessagePreview : undefined,
        });
      });
      onThreads(rows);
    },
    (err) => {
      console.error('[thesconAiThreads] thread list subscription failed', err);
      onThreads([]);
    }
  );
}

export async function appendMessage(
  db: Firestore,
  threadId: string,
  messageId: string,
  payload: Omit<ThreadMessageDoc, 'createdAt'>
): Promise<void> {
  // Firestore rejects undefined values — omit optional fields instead of spreading payload.
  const row: Record<string, unknown> = {
    role: payload.role,
    content: payload.content,
    createdAt: serverTimestamp(),
  };
  if (payload.retrievalJobId != null && payload.retrievalJobId !== '') {
    row.retrievalJobId = payload.retrievalJobId;
  }
  await setDoc(doc(db, THESCON_AI_THREADS_COLLECTION, threadId, 'messages', messageId), row);
}

/** When falling back from staged retrieval to askKnowledge, drop job linkage on the user turn. */
export async function clearMessageRetrievalJob(
  db: Firestore,
  threadId: string,
  messageId: string
): Promise<void> {
  await updateDoc(doc(db, THESCON_AI_THREADS_COLLECTION, threadId, 'messages', messageId), {
    retrievalJobId: deleteField(),
  });
}

/** Subscribe to messages ordered by creation time (ascending). */
export function subscribeThreadMessages(
  db: Firestore,
  threadId: string,
  onMessages: (rows: Array<{ id: string; doc: ThreadMessageDoc }>) => void
): Unsubscribe {
  const q = query(messagesCollectionRef(db, threadId), orderBy('createdAt', 'asc'));
  return onSnapshot(
    q,
    (snap) => {
      const rows: Array<{ id: string; doc: ThreadMessageDoc }> = [];
      snap.forEach((d) => {
        const data = d.data() as ThreadMessageDoc;
        rows.push({ id: d.id, doc: data });
      });
      onMessages(rows);
    },
    (err) => {
      console.error('[thesconAiThreads] messages subscription failed', err);
      onMessages([]);
    }
  );
}

export async function deleteThreadAndMessages(db: Firestore, threadId: string): Promise<void> {
  const msgsRef = messagesCollectionRef(db, threadId);
  const snapshot = await getDocs(msgsRef);
  const docs = snapshot.docs;
  const chunkSize = 450;
  for (let i = 0; i < docs.length; i += chunkSize) {
    const slice = docs.slice(i, i + chunkSize);
    const batch = writeBatch(db);
    slice.forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }
  await deleteDoc(doc(db, THESCON_AI_THREADS_COLLECTION, threadId));
}
